/**
 * Ecommerce-metrics SSE pipeline → store subscriber.
 * Migrated in Phase 8 commit 77.
 *
 * Re-uses the `updateSubPhase` pattern from the ecommerce subscriber —
 * each event flips a single sub-phase to running/complete inside
 * `campaignData.ecommerceSubPhases`. The subscriber owns merging the
 * product-metrics arrays via `mergeProductMetrics` so the runner stays
 * pure.
 */

import { sseEvents } from "./sseEvents";
import { mergeProductMetrics } from "../store/department/utils";
import { toThoughtEvent } from "../store/department/thoughtStream";
import type {
  CampaignData,
  StepStatus,
  ThoughtEvent,
} from "../store/department/types";

interface EcommerceMetricsSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  addThoughtEvent: (stepIndex: number, event: Omit<ThoughtEvent, "id" | "ts">) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  getCampaignData: () => CampaignData;
}

let wired = false;

export function wireEcommerceMetricsSubscriber(
  getHooks: () => EcommerceMetricsSubscriberHooks,
): void {
  if (wired) return;
  wired = true;

  const thought = (
    step: number,
    phase: string,
    status: string,
    data?: Record<string, unknown>,
    progress?: Record<string, unknown>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ev = toThoughtEvent("ecommerce", phase, status, data as any, progress as any);
    if (ev) getHooks().addThoughtEvent(step, ev);
  };

  const updateSubPhase = (
    phaseKey: string,
    newStatus: "pending" | "running" | "complete" | "error",
  ) => {
    const hooks = getHooks();
    const current = hooks.getCampaignData().ecommerceSubPhases;
    if (!current) return;
    hooks.updateCampaignData({
      ecommerceSubPhases: current.map((sp) =>
        sp.phase === phaseKey ? { ...sp, status: newStatus } : sp,
      ),
    });
  };

  sseEvents.subscribe("ecommerce-metrics:metrics-calculation-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "metrics_calculation",
      ecommerceResearchProgress: { phase: "metrics_calculation", completed: 4, total: 8 },
    });
    updateSubPhase("metrics_calculation", "running");
    thought(1, "fetch_orders", "running");
    thought(1, "unit_economics", "running");
    hooks.addStepLog(1, "Computing unit economics and order metrics...");
  });

  sseEvents.subscribe("ecommerce-metrics:metrics-calculation-complete", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "metrics_calculation", completed: 5, total: 8 },
      ecommerceMetrics: data as CampaignData["ecommerceMetrics"],
    });
    updateSubPhase("metrics_calculation", "complete");
    thought(1, "fetch_orders", "complete", data);
    thought(1, "unit_economics", "complete", data);
    hooks.addStepLog(1, `[SUCCESS] AOV: $${data.avg_order_value ?? 0}`);
  });

  sseEvents.subscribe("ecommerce-metrics:priority-scoring-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "priority_scoring",
      ecommerceResearchProgress: { phase: "priority_scoring", completed: 5, total: 8 },
    });
    updateSubPhase("priority_scoring", "running");
    thought(1, "scoring", "running");
    hooks.addStepLog(1, "Scoring per-product ad readiness...");
  });

  sseEvents.subscribe("ecommerce-metrics:priority-scoring-complete", ({ data }) => {
    const hooks = getHooks();
    const productMetrics = (data.productMetrics ?? []) as unknown[];
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "priority_scoring", completed: 6, total: 8 },
      ecommerceProductMetrics: mergeProductMetrics(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        productMetrics as any,
        hooks.getCampaignData().ecommerceProductMetrics,
      ),
      ecommerceSubPhases: [
        { phase: "ad_cost_estimation", label: "Estimating ad costs", status: "pending" },
        { phase: "ad_cost_synthesis", label: "Synthesizing strategy", status: "pending" },
      ],
    });
    updateSubPhase("priority_scoring", "complete");
    const strong = productMetrics.filter((m) => {
      const verdict = (m as { verdict?: unknown }).verdict;
      return verdict === "strong" || verdict === "viable";
    }).length;
    thought(1, "scoring", "complete", { viable: strong });
    hooks.addStepLog(
      1,
      `[SUCCESS] ${data.count ?? 0} products scored — ${strong} viable for ads`,
    );
  });

  sseEvents.subscribe("ecommerce-metrics:ad-cost-estimation-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "ad_cost_estimation",
      ecommerceResearchProgress: { phase: "ad_cost_estimation", completed: 6, total: 8 },
    });
    updateSubPhase("ad_cost_estimation", "running");
    thought(1, "ad_cost_google", "running");
    thought(1, "ad_cost_meta", "running");
    thought(1, "ad_cost_tiktok", "running");
    thought(1, "ad_cost_estimation", "running");
    hooks.addStepLog(1, "Estimating ad costs across Google, Meta, TikTok...");
  });

  sseEvents.subscribe("ecommerce-metrics:ad-cost-estimation-complete", ({ data }) => {
    const hooks = getHooks();
    const enriched = data.enrichedProductMetrics as unknown[] | undefined;
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "ad_cost_estimation", completed: 7, total: 8 },
      ecommerceAdCosts: (data.adCostEstimates ?? []) as CampaignData["ecommerceAdCosts"],
      ecommerceProductMetrics: enriched
        ? mergeProductMetrics(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            enriched as any,
            hooks.getCampaignData().ecommerceProductMetrics,
          )
        : hooks.getCampaignData().ecommerceProductMetrics,
    });
    updateSubPhase("ad_cost_estimation", "complete");
    thought(1, "weighted_cpa", "complete");
    const estimates = data.adCostEstimates as unknown[] | undefined;
    const estimated = Array.isArray(estimates) ? estimates.length : 0;
    thought(1, "ad_cost_estimation", "complete", { count: estimated });
    hooks.addStepLog(1, `[SUCCESS] Ad costs estimated for ${estimated} products`);
  });

  sseEvents.subscribe("ecommerce-metrics:ad-cost-synthesis-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "ad_cost_synthesis",
      ecommerceResearchProgress: { phase: "ad_cost_synthesis", completed: 7, total: 8 },
    });
    updateSubPhase("ad_cost_synthesis", "running");
    thought(1, "portfolio_ranking", "running");
    thought(1, "budget_scenarios", "running");
    thought(1, "strategic_insights", "running");
    thought(1, "portfolio_synthesis", "running");
    hooks.addStepLog(1, "Building portfolio strategy and budget scenarios...");
  });

  sseEvents.subscribe("ecommerce-metrics:ad-cost-synthesis-complete", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "ad_cost_synthesis", completed: 8, total: 8 },
      ecommercePortfolioSynthesis:
        (data.portfolioSynthesis ?? null) as CampaignData["ecommercePortfolioSynthesis"],
      ecommerceGeminiInsights:
        (data.geminiInsights ?? {}) as CampaignData["ecommerceGeminiInsights"],
    });
    updateSubPhase("ad_cost_synthesis", "complete");
    thought(1, "strategic_insights", "complete");
    thought(1, "portfolio_synthesis", "complete");
    const portfolio = data.portfolioSynthesis as
      | { summary?: { adReadyProducts?: number } }
      | undefined;
    const adReady = portfolio?.summary?.adReadyProducts ?? 0;
    hooks.addStepLog(1, `[SUCCESS] Portfolio synthesis — ${adReady} ad-ready products`);
  });

  sseEvents.subscribe("ecommerce-metrics:done", ({ data }) => {
    const hooks = getHooks();
    thought(1, "done", "complete");
    const productMetrics = data.productMetrics as unknown[] | undefined;
    hooks.updateCampaignData({
      ecommerceResearchStatus: "complete",
      ecommerceResearchPhase: "done",
      ecommerceResearchProgress: { phase: "done", completed: 8, total: 8 },
      ecommerceMetrics: data.metrics as CampaignData["ecommerceMetrics"],
      ecommerceProductMetrics: productMetrics
        ? mergeProductMetrics(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            productMetrics as any,
            hooks.getCampaignData().ecommerceProductMetrics,
          )
        : hooks.getCampaignData().ecommerceProductMetrics,
      ecommerceAdCosts: data.adCostEstimates as CampaignData["ecommerceAdCosts"],
      ecommercePortfolioSynthesis:
        data.portfolioSynthesis as CampaignData["ecommercePortfolioSynthesis"],
      ecommerceGeminiInsights:
        data.geminiInsights as CampaignData["ecommerceGeminiInsights"],
      totalCreditsUsed:
        Number(hooks.getCampaignData().totalCreditsUsed ?? 0) +
        Number(data.totalCreditsUsed ?? 0),
      ecommerceSubPhases: undefined,
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 1,
      stepStatuses: { ...state.stepStatuses, 1: "waiting" },
    }));
  });

  sseEvents.subscribe("ecommerce-metrics:error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({ ecommerceResearchPhase: "error" });
    thought(1, "error", "error", { message });
    hooks.addStepLog(1, `[ERROR] ${message}`);
    hooks.setStepState((state) => ({
      currentStepIndex: 1,
      stepStatuses: { ...state.stepStatuses, 1: "waiting" },
    }));
  });
}
